import { StationeryTemplateCategoryEnum } from '@graphql/generated';
import { useCallback, useMemo } from 'react';
import { StationeryTemplate } from './DesignsGallery.types';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useFeatureValue } from '@shared/core/featureFlags';
import { useHistory } from '@react-router';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import useConciergeTiles from './useConciergeTiles';
import { doesProductComponentMatch } from '@apps/card/routes/CardCustomizer/usePromotionConfig';
import { useCachedRandom } from './useCachedRandom';
import { useDesignGalleryFiltersContext } from './DesignGalleryFiltersProvider';
import { cloneDeep } from 'lodash-es';
import { useApplySearchFiltersToTemplates } from './useApplySearchFiltersToTemplates';
import { useDefaultMediaMatchContext } from '@shared/utils/media/DefaultMediaMatchProvider';
import { GalleryItemData } from './components/GalleryItem/GalleryItem';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { usePropValuesData } from './usePropValuesData';
import type { PropAsset, PropsPayload } from './propValues.types';

const isCategoryRepeatBlocklisted = (category: StationeryTemplateCategoryEnum, payload: PropsPayload) => {
  if (!payload.repeatTilesCategoryBlocklist) {
    return false;
  }

  return payload.repeatTilesCategoryBlocklist.includes(category);
};

const cardCategoryMatches = (categoriesList: string[], context: { occasion: string; category: StationeryTemplateCategoryEnum }) => {
  return categoriesList.some(category => {
    const categoryComponents = category.split('/');
    const [targetOccasion, targetCategory] = categoryComponents;
    const { category: currentCategory, occasion: currentOccasion } = context;

    if (!doesProductComponentMatch(targetOccasion, currentOccasion)) {
      return false;
    }

    if (!doesProductComponentMatch(targetCategory, currentCategory)) {
      return false;
    }

    return true;
  });
};

const injectImagineCard = (templatesWithProps: GalleryItemData[], category: StationeryTemplateCategoryEnum, onClickCards: () => void) => {
  const imagineProps = [
    {
      desktop: 'https://withjoy.com/media/raw/print/savethedate-simple.gif',
      mobile: 'https://withjoy.com/media/raw/print/savethedate-simple.gif',
      category: StationeryTemplateCategoryEnum.saveTheDate,
      onClick: onClickCards,
      titleText: 'Imagine Your Card',
    },
    {
      desktop: 'https://withjoy.com/media/raw/print/invitation-fade-720x1008.gif',
      mobile: 'https://withjoy.com/media/raw/print/invitation-simple.gif',
      category: StationeryTemplateCategoryEnum.invitation,
      onClick: onClickCards,
      titleText: 'Imagine Your Card',
    },
  ];

  imagineProps.forEach(prop => {
    if (prop.category === category) {
      templatesWithProps.splice(2, 0, {
        ...prop!,
        type: 'cardlink',
      });
    }
  });
};

// Checks to see if the specified row + column is already
// taken by a manually positioned card. Returns the closest avaliable
// position, and offset for tracking rolling offset of cards downstream.
const checkForOverlap = (row: number, column: number, rowGap: number, furthestColumn: number, cards: PropAsset[]) => {
  let offset = 0;
  cards.forEach(card => {
    if (card.preferredPosition) {
      // Calculate comparison card start & end position
      const preferredColumn = card.preferredPosition.column;
      const columnSpilloverRows = Math.floor(preferredColumn / furthestColumn);
      const actualColumnStart = preferredColumn % furthestColumn;
      const actualColumnEnd = actualColumnStart + (card.columns ?? 1) - 1;
      const actualRow = card.preferredPosition.row + columnSpilloverRows;

      // Check if we overlap target card.
      if (row == actualRow && column >= actualColumnStart && column <= actualColumnEnd) {
        offset += 1 + rowGap;
        return;
      }
    }
  });

  return { row: row + offset, column, offset: -offset };
};

const RESPONSIVE_CONFIG = {
  _: { row: 2, rowGap: 2 },
  sm2: { row: 3, rowGap: 2 },
  md3: { row: 4, rowGap: 1 },
  lg: { row: 5, rowGap: 1 },
  xl: { row: 6, rowGap: 1 },
};

export const usePropValues = (templates: ReadonlyArray<StationeryTemplate>, isSearchActive: boolean) => {
  const { currentCategory: category } = useCardCategoryContext();
  const { value: showImagineTab } = useFeatureValue('printImagineGalleryEnabled');
  const { value: showImagineProps } = useFeatureValue('printImagineShowValueProps');
  const validatedPayload = usePropValuesData();
  const { getTotalActiveFilterCount } = useDesignGalleryFiltersContext();
  const history = useHistory();
  const { getCardImaginePath } = useCardsRouterContext();
  const injectConcierge = useConciergeTiles(category);
  const cachedRandom = useCachedRandom({ deps: [category] });
  const shuffleRandom = useCachedRandom({ deps: [category] });

  const { useSearchResults } = useApplySearchFiltersToTemplates();
  const searchResults = useSearchResults();
  const { searchCompleted } = searchResults;
  const isCategoryWithConciergeService = [StationeryTemplateCategoryEnum.invitation, StationeryTemplateCategoryEnum.saveTheDate].includes(category);
  const conciergeEnabled = isCategoryWithConciergeService && !searchCompleted;

  const goToImagine = useCallback(() => {
    history.push(getCardImaginePath('wedding', category));
  }, [category, getCardImaginePath, history]);

  const { defaultScreen } = useDefaultMediaMatchContext();

  // row - number of cards per row, rowGap - number of empty rows between cards
  const [config] = useResponsive(
    {
      values: RESPONSIVE_CONFIG,
    },
    defaultScreen === 'desktop' ? RESPONSIVE_CONFIG.lg : defaultScreen === 'tablet' ? RESPONSIVE_CONFIG.md3 : RESPONSIVE_CONFIG._,
  );

  const shouldInjectProps = getTotalActiveFilterCount() === 0 && !searchCompleted && !isSearchActive;

  const tiles = useMemo(() => {
    const templatesWithProps: GalleryItemData[] = [...templates].map(t => ({
      ...t,
      type: 'template',
    }));

    if (!shouldInjectProps) {
      return templatesWithProps;
    }

    let cards: PropAsset[] = [];

    const totalRows = templates.length / (config?.row ?? 1);
    const requiredPropsToFillGallery = totalRows / ((config?.rowGap ?? 1) + 1);

    if (validatedPayload) {
      const originalValuePropsList = validatedPayload.props.filter(card => {
        return cardCategoryMatches(card.categoryList, {
          occasion: 'wedding',
          category,
        });
      });

      cards = cloneDeep(originalValuePropsList);

      if ((validatedPayload.repeatTiles ?? false) && !isCategoryRepeatBlocklisted(category, validatedPayload)) {
        const shuffledPool = originalValuePropsList.sort(a => {
          return shuffleRandom(originalValuePropsList.indexOf(a)) - 0.5;
        });

        if (cards.length < requiredPropsToFillGallery) {
          for (let i = cards.length; i < requiredPropsToFillGallery; i++) {
            const card = shuffledPool[i % originalValuePropsList.length];
            // Do not repeat cards with specified positions.
            if (!card.preferredPosition) {
              cards.push(card);
            }
          }
        }
      }
    }

    const shouldInjectFeatured = showImagineTab === 'on' && showImagineProps === 'on';
    if (shouldInjectFeatured) {
      injectImagineCard(templatesWithProps, category, goToImagine);
    }

    if (config) {
      // Track how many cards are not following normal positioning
      // So we can account for that in the following cards.
      let rowRollingOffset = 0;
      // Make sure we do not place more props than can fit.
      const maxRows = (templatesWithProps.length + cards.length) / (config.row * (config.rowGap + 1)) - 1;
      const manuallyPositionedCards = cards.filter(prop => !!prop.preferredPosition);

      cards.forEach((card, row) => {
        if (row >= maxRows) {
          return;
        }

        const furthestColumn = Math.max(config.row - (card.columns ?? 1 - 1), 1);
        if (!card.preferredPosition) {
          // Randomly positioned cards
          const positionOnRow = Math.floor(cachedRandom(row) * furthestColumn);
          const cardRow = (config.rowGap + 1) * (row - rowRollingOffset + 1);
          const { row: safeRow, column: safeCol, offset } = checkForOverlap(cardRow, positionOnRow, config.rowGap, furthestColumn, manuallyPositionedCards);
          card.row = safeRow;
          card.column = safeCol;
          rowRollingOffset += offset;
        } else {
          // Manually positioned cards
          const preferredColumn = card.preferredPosition.column;
          const columnSpilloverRows = Math.floor(preferredColumn / furthestColumn);
          card.column = preferredColumn % furthestColumn;
          card.row = card.preferredPosition.row + columnSpilloverRows;
          rowRollingOffset++;
        }
        templatesWithProps.push({ ...card, type: 'prop' });
      });
    }

    if (conciergeEnabled) {
      return injectConcierge(templatesWithProps, category);
    }

    return templatesWithProps;
  }, [
    templates,
    shouldInjectProps,
    config,
    validatedPayload,
    showImagineTab,
    showImagineProps,
    category,
    goToImagine,
    conciergeEnabled,
    shuffleRandom,
    cachedRandom,
    injectConcierge,
  ]);

  return {
    templatesWithProps: tiles,
    galleryColumnCount: config.row,
  };
};
